import React from 'react';
import { motion } from 'framer-motion';
import { Star, Quote } from 'lucide-react';
import { TESTIMONIALS } from '../utils/constants';
import ScrollReveal from './ScrollReveal';

const TestimonialsSection = () => {
  return (
    <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <ScrollReveal direction="up">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center mb-6">
              <div className="h-px w-20 bg-gradient-to-r from-transparent to-gold-400" />
              <div className="mx-4 w-2 h-2 bg-gold-400 rotate-45" />
              <div className="h-px w-20 bg-gradient-to-l from-transparent to-gold-400" />
            </div>
            
            <h2 className="text-5xl md:text-6xl font-serif text-navy-900 mb-6">
              Guest Experiences
            </h2>
            
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light">
              Don't just take our word for it. Hear what our valued guests have to say about their stay.
            </p>
          </div>
        </ScrollReveal>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {TESTIMONIALS.map((testimonial, index) => (
            <ScrollReveal key={index} direction="up" delay={index * 0.1}>
              <motion.div
                whileHover={{ y: -10 }}
                className="bg-white rounded-lg p-8 shadow-lg hover:shadow-2xl transition-all duration-500 relative group"
              >
                {/* Quote Icon */}
                <motion.div 
                  whileHover={{ scale: 1.1 }}
                  className="absolute -top-4 left-8 w-12 h-12 bg-gold-400 rounded-full flex items-center justify-center shadow-lg"
                >
                  <Quote className="w-6 h-6 text-navy-900" />
                </motion.div>

                {/* Rating */}
                <div className="flex items-center space-x-1 mb-4 mt-4">
                  {[...Array(testimonial.rating)].map((_, idx) => (
                    <Star key={idx} className="w-5 h-5 text-gold-400 fill-gold-400" />
                  ))}
                </div>

                {/* Review Text */}
                <p className="text-gray-700 leading-relaxed mb-6 italic">
                  "{testimonial.review}"
                </p>

                {/* Divider */}
                <div className="h-px bg-gradient-to-r from-gold-400 via-gold-400/50 to-transparent mb-4" />

                {/* Author Info */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-navy-900">{testimonial.name}</p>
                    <p className="text-sm text-gold-500">{testimonial.property}</p>
                  </div>
                  <p className="text-xs text-gray-400">{testimonial.date}</p>
                </div>

                {/* Decorative Corner */}
                <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-gold-400/20 rounded-br-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </motion.div>
            </ScrollReveal>
          ))}
        </div>

        {/* Bottom Stats */}
        <ScrollReveal direction="up" delay={0.3}>
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {[
              { value: "4.5+", label: "Average Rating" },
              { value: "1,500+", label: "Happy Guests" },
              { value: "500+", label: "Events Hosted" },
              { value: "15+", label: "Years of Excellence" }
            ].map((stat, index) => (
              <motion.div 
                key={index}
                className="text-center"
                whileHover={{ scale: 1.05 }}
              >
                <div className="text-4xl font-serif text-gold-500 mb-2">{stat.value}</div>
                <div className="text-gray-600 font-light">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </ScrollReveal>

        {/* CTA */}
        <ScrollReveal direction="up" delay={0.4}>
          <div className="text-center mt-16">
            <p className="text-gray-600 mb-6 text-lg">
              Ready to create your own memorable experience?
            </p>
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-10 py-4 bg-navy-900 text-white hover:bg-gold-400 hover:text-navy-900 transition-all duration-300 font-semibold tracking-wider rounded shadow-lg hover:shadow-2xl"
            >
              BOOK YOUR STAY
            </motion.button>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default TestimonialsSection;
